import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bookmark,
  Briefcase,
  ChevronRight,
  Eye,
  Send,
  ShieldCheck,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BackButton from "../shared/backButton";
import MessageManager from "../layout/Message";
import {
  getProfileShortlistFlag,
  isProfileInRoleShortlist,
  readHiringShortlists,
  subscribeHiringShortlists,
  addProfilesToRoleShortlist,
  removeProfilesFromRoleShortlist,
} from "../shared/utils/hiringShortlistState";
import {
  getTop3RoleMatchesOfUserIdApi,
  shortListPersonForJobRoleApi,
  removePersonFromShortlistApi,
  contactPartnerApi,
} from "../../services/hiringService";
import "./HiringProfileDetailPage.css";

const readStoredDetail = () => {
  try {
    const stored = sessionStorage.getItem("mmhHiringProfileDetail");
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Unable to read hiring profile detail state:", error);
    return null;
  }
};

const DEFAULT_PARTNER_MESSAGE =
  "Hi, we're interested in this profile and would appreciate any additional information you can share. Thank you!";

const getMatchAnalysis = (profile, activeRole) => {
  const targetTitle = activeRole?.title || "the selected role";

  const targetDepartment = activeRole?.department || "the selected department";

  const targetLevel = activeRole?.level || "the selected level";

  const sourceLabel = profile?.source || "Unknown";

  return {
    heading: `${profile?.match || 0}% match for ${targetTitle}`,

    comparedAgainst: `${targetTitle} \u00B7 ${targetDepartment} \u00B7 ${targetLevel}`,

    summary: `${sourceLabel} profile assessed against the role requirements, available profile information, and current hiring context.`,

    breakdown: [
      {
        label: "Profile signal",
        value: profile?.title || "-",
        detail: profile?.department || "-",
      },
      {
        label: "Availability",
        value: profile?.availability || "-",
        detail: profile?.partner
          ? `Shared by ${profile.partner}`
          : "Your organization",
      },
    ],
  };
};

const inFlightTopRoleMatchRequests = new Map();

const getTop3RoleMatchesOfUserIdApiHandler = async (profileId) => {
  const userId = String(profileId || "").trim();

  if (!userId) {
    return [];
  }

  const requestKey = JSON.stringify({ userId });
  const existingRequest = inFlightTopRoleMatchRequests.get(requestKey);

  if (existingRequest) {
    return existingRequest;
  }

  try {
    const request = getTop3RoleMatchesOfUserIdApi({ userId })
      .then((response) => {
        const matches = [response?.data?.data, response?.data, response].find(
          Array.isArray,
        );

        return matches || [];
      })
      .catch((error) => {
        console.error("Failed to fetch top 3 role matches:", error);
        return [];
      })
      .finally(() => {
        if (inFlightTopRoleMatchRequests.get(requestKey) === request) {
          inFlightTopRoleMatchRequests.delete(requestKey);
        }
      });

    inFlightTopRoleMatchRequests.set(requestKey, request);

    return request;
  } catch (error) {
    console.error("Failed to fetch top 3 role matches:", error);
    return [];
  }
};

const getMatchProfileUserId = (profile) =>
  String(
    profile?.userId || profile?.rawProfile?.userId || profile?.id || "",
  ).trim();

const isProtectedExternalProfileName = (name) =>
  String(name || "")
    .trim()
    .toUpperCase()
    .startsWith("EXT");

const ensureShortlistApiSuccess = (response) => {
  const code = Number(response?.code);

  if (Number.isFinite(code) && ![200, 201].includes(code)) {
    throw new Error(response?.message || "The shortlist request failed.");
  }

  return response;
};

const clampPercentage = (value) => {
  const percentage = Number(value);

  if (!Number.isFinite(percentage)) return 0;

  return Math.max(0, Math.min(100, percentage));
};

const normalizeTopRoleMatch = (matchedRole, index) => ({
  id: String(matchedRole?.jobId || matchedRole?._id || `top-role-${index + 1}`),
  jobId: matchedRole?.jobId || "",
  title: matchedRole?.roleName || matchedRole?.title || "Untitled role",
  department: matchedRole?.department || "-",
  level: matchedRole?.level || "-",
  score: clampPercentage(matchedRole?.totalMatchPercentage),
  rawRole: matchedRole,
});

const HiringProfileDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profileId } = useParams();
  const messageFieldRef = useRef(null);

  const detail = useMemo(
    () => location.state || readStoredDetail(),
    [location.state],
  );

  const [profileContext, setProfileContext] = useState(() => detail || null);
  const profile = profileContext?.profile;
  const role = profileContext?.role;
  const resolvedProfileId = profile?.name || "";
  const resolvedTopRoleMatchUserId =
    profile?.userId || profile?.rawProfile?.userId || profile?.id || profileId;

  const [shortlists, setShortlists] = useState(readHiringShortlists);
  const [topRoleMatches, setTopRoleMatches] = useState([]);
  const [topRoleMatchesLoading, setTopRoleMatchesLoading] = useState(false);
  const [shortlistActionLoading, setShortlistActionLoading] = useState(false);

  useEffect(() => {
    setProfileContext(detail || null);
  }, [detail]);

  useEffect(() => {
    return subscribeHiringShortlists(setShortlists);
  }, []);

  useEffect(() => {
    const userId = String(resolvedTopRoleMatchUserId || "").trim();

    if (!userId) {
      setTopRoleMatches([]);
      setTopRoleMatchesLoading(false);
      return;
    }

    let isCurrentRequest = true;

    const fetchTopRoleMatches = async () => {
      setTopRoleMatchesLoading(true);

      const matches = await getTop3RoleMatchesOfUserIdApiHandler(userId);

      if (!isCurrentRequest) return;

      setTopRoleMatches(matches.map(normalizeTopRoleMatch));
      setTopRoleMatchesLoading(false);
    };

    fetchTopRoleMatches();

    return () => {
      isCurrentRequest = false;
    };
  }, [resolvedTopRoleMatchUserId]);

  const apiShortlistFlag = getProfileShortlistFlag(profile);
  const isSaved =
    typeof apiShortlistFlag === "boolean"
      ? apiShortlistFlag
      : isProfileInRoleShortlist(shortlists, role?.id, profile?.id);
  const [message, setMessage] = useState(() =>
    profile?.isInternal ? "" : DEFAULT_PARTNER_MESSAGE,
  );

  const analysis = useMemo(
    () => (profile && role ? getMatchAnalysis(profile, role) : null),
    [profile, role],
  );

  const [bestRoleMatch, ...additionalRoleMatches] = topRoleMatches;

  useEffect(() => {
    setMessage(profile?.isInternal ? "" : DEFAULT_PARTNER_MESSAGE);
  }, [profile?.id, profile?.isInternal]);


  const handleToggleSave = async () => {
    if (shortlistActionLoading) return;

    const userId = getMatchProfileUserId(profile);

    if (!role?.id || !role?.jobId || !userId) {
      MessageManager.showToast(
        "error",
        "Unable to update shortlist for this profile.",
      );
      return;
    }

    const shouldRemove = isSaved;

    try {
      setShortlistActionLoading(true);

      const requestData = {
        jobId: role.jobId,
        userIdArray: [userId],
      };

      const response = shouldRemove
        ? await removePersonFromShortlistApi(requestData)
        : await shortListPersonForJobRoleApi(requestData);

      ensureShortlistApiSuccess(response);

      const nextProfile = {
        ...profile,
        saved: !shouldRemove,
      };
      const nextContext = {
        ...(profileContext || {}),
        profile: nextProfile,
        role,
        returnView: detail?.returnView || "matches",
      };
      const nextShortlists = shouldRemove
        ? removeProfilesFromRoleShortlist(role.id, [profile.id])
        : addProfilesToRoleShortlist(role, [nextProfile]);

      setShortlists(nextShortlists);
      setProfileContext(nextContext);
      sessionStorage.setItem(
        "mmhHiringProfileDetail",
        JSON.stringify(nextContext),
      );

      MessageManager.showToast(
        "success",
        shouldRemove ? "Removed from shortlist" : "Added to shortlist",
      );
    } catch (error) {
      console.error("Failed to update shortlist:", error);
      MessageManager.showToast(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to update shortlist.",
      );
    } finally {
      setShortlistActionLoading(false);
    }
  };

  const handleViewFullProfile = () => {
    if (profile.isInternal) {
      navigate(`/employees/${encodeURIComponent(profile.id)}`, {
        state: {
          profile,
          role,
          returnView: detail?.returnView || "matches",
        },
      });

      return;
    }

    const fullProfileContext = {
      profile: {
        ...profile,
        roleMatches: topRoleMatches.map((matchedRole) => ({
          jobId: matchedRole.jobId,
          title: matchedRole.title,
          department: matchedRole.department,
          level: matchedRole.level,
          match: matchedRole.score,
        })),
      },
      role,
      returnView: detail?.returnView || "matches",
    };

    sessionStorage.setItem(
      "mmhExternalCandidateProfile",
      JSON.stringify(fullProfileContext),
    );

    navigate(
      `/talent-development/hiring/external-profiles/${encodeURIComponent(profile.id)}`,
      {
        state: fullProfileContext,
      },
    );
  };

  const handleContactPartner = () => {
    messageFieldRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    window.setTimeout(() => messageFieldRef.current?.focus(), 250);
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !profile || profile.isInternal) return;

    try {
      const obj = {
        partnerId: profile.partnerId,
        jobId: role.jobId,
        userId: profile.userId,
        message: message,
      };

      const response = await contactPartnerApi(obj);

      if (response.code == 200) {
        MessageManager.showToast("success", "Partner contacted");
        setMessage("");
      } else {
        MessageManager.showToast(
          "error",
          response.message || "Unable to send message.",
        );
      }
    } catch (error) {
      MessageManager.showToast(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to send message.",
      );
    }
  };

  if (!profile || !role || !analysis) {
    return (
      <div className="mmh-epd-page">
        <main className="mmh-epd-inner">
          <BackButton />

          <section className="mmh-epd-missing-state">
            <Briefcase size={28} />
            <h1>Profile context is unavailable</h1>
            <p>
              Open this profile from the Hiring match list to view its match
              details.
            </p>
            <button
              type="button"
              className="mmh-epd-primary-button"
              onClick={() => navigate("/hiring")}
            >
              Return to Hiring
            </button>
          </section>
        </main>
      </div>
    );
  }

  const profileTitle = profile.name || "";
  const isIdentityProtected =
    !profile.isInternal && isProtectedExternalProfileName(profileTitle);

  return (
    <div className="mmh-epd-page">
      <main className="mmh-epd-inner">
        <BackButton />

        <nav className="mmh-epd-breadcrumb" aria-label="Breadcrumb">
          <button
            type="button"
            onClick={() => navigate("/talent-development/hiring")}
          >
            Hiring
          </button>
          <ChevronRight size={14} />
          <span>{role.title}</span>
          <ChevronRight size={14} />
          <strong>{resolvedProfileId}</strong>
        </nav>

        <section className="mmh-epd-shell">
          <header className="mmh-epd-header">
            <div className="mmh-epd-title-block">
              <h1>{profileTitle}</h1>

              <div className="mmh-epd-badge-row">
                {isIdentityProtected && (
                  <span className="mmh-epd-badge mmh-epd-badge-protected">
                    <ShieldCheck size={12} />
                    Identity Protected
                  </span>
                )}

                <span
                  className={`mmh-epd-badge ${
                    profile.isInternal
                      ? "mmh-epd-badge-internal"
                      : "mmh-epd-badge-external"
                  }`}
                >
                  {profile.source || "Unknown"}
                </span>
              </div>
            </div>

            <div className="mmh-epd-header-actions">
              <button
                type="button"
                className="mmh-epd-secondary-button"
                onClick={handleToggleSave}
                disabled={shortlistActionLoading}
              >
                <Bookmark size={16} fill={isSaved ? "currentColor" : "none"} />

                {shortlistActionLoading
                  ? isSaved
                    ? "Removing..."
                    : "Adding..."
                  : isSaved
                    ? "Remove from Shortlist"
                    : "Add to Shortlist"}
              </button>

              <button
                type="button"
                className="mmh-epd-secondary-button"
                onClick={handleViewFullProfile}
              >
                <Eye size={16} />
                View Full Profile
              </button>

              {!profile.isInternal && (
                <button
                  type="button"
                  className="mmh-epd-primary-button"
                  onClick={handleContactPartner}
                >
                  <Send size={16} />
                  Contact Partner
                </button>
              )}
            </div>
          </header>

          <div className="mmh-epd-content">
            <div className="mmh-epd-analysis-layout">
              <section className="mmh-epd-current-match-card">
                <div className="mmh-epd-current-match-top">
                  <div>
                    <span className="mmh-epd-eyebrow">Match analysis</span>
                    <h2>{analysis.heading}</h2>
                    <p>{analysis.summary}</p>
                  </div>

                  <div className="mmh-epd-score-ring">
                    <strong>{profile.match}%</strong>
                    <span>Match</span>
                  </div>
                </div>

                <div className="mmh-epd-context-strip">
                  {analysis.comparedAgainst}
                </div>

                <div className="mmh-epd-breakdown-list">
                  {analysis.breakdown.map((item) => (
                    <div key={item.label}>
                      <span>{item.label}</span>
                      <div>
                        <strong>{item.value}</strong>
                        <small>{item.detail}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mmh-epd-role-match-section">
                <div className="mmh-epd-section-title">
                  <h2>Top role matches</h2>
                  <p>
                    Top 3 roles that best match this profile across all roles in
                    your organization.
                  </p>
                </div>

                {topRoleMatchesLoading ? (
                  <p className="mmh-epd-role-match-state">
                    Loading role matches...
                  </p>
                ) : topRoleMatches.length > 0 ? (
                  <>
                    {bestRoleMatch && (
                      <div className="mmh-epd-featured-role-card">
                        <div className="mmh-epd-role-rank">01</div>

                        <div className="mmh-epd-role-main">
                          <div className="mmh-epd-role-title-row">
                            <div>
                              <span>Best match</span>
                              <strong>{bestRoleMatch.title}</strong>
                            </div>
                            <div className="mmh-epd-role-score">
                              {bestRoleMatch.score}%
                            </div>
                          </div>

                          <div className="mmh-epd-role-meta">
                            <span>{bestRoleMatch.department}</span>
                            <span>{bestRoleMatch.level}</span>
                          </div>

                          <div className="mmh-epd-role-meter">
                            <span
                              style={{ width: `${bestRoleMatch.score}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {additionalRoleMatches.length > 0 && (
                      <div className="mmh-epd-mini-role-grid">
                        {additionalRoleMatches.map((matchedRole, index) => (
                          <div
                            key={matchedRole.id}
                            className="mmh-epd-mini-role-card"
                          >
                            <div className="mmh-epd-mini-role-head">
                              <span>{String(index + 2).padStart(2, "0")}</span>
                              <strong>{matchedRole.score}%</strong>
                            </div>

                            <h3>{matchedRole.title}</h3>
                            <p>{matchedRole.department}</p>
                            <small>{matchedRole.level}</small>

                            <div className="mmh-epd-role-meter mmh-epd-role-meter-compact">
                              <span
                                style={{ width: `${matchedRole.score}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="mmh-epd-role-match-state">
                    No role matches available.
                  </p>
                )}
              </section>
            </div>

            {!profile.isInternal && (
              <section className="mmh-epd-contact-card">
                <div className="mmh-epd-contact-heading">
                  <div>
                    <h2>Contact Partner About Profile {profileTitle}</h2>
                  </div>
                </div>

                <div className="mmh-epd-contact-context">
                  <div>
                    <strong>Partner:</strong> {profile.partner}
                  </div>
                  <div>
                    <strong>Profile:</strong> {profileTitle}
                  </div>
                  <div>
                    <strong>Role:</strong> {role.title}
                  </div>
                  <div>
                    <strong>Match:</strong> {profile.match}%
                  </div>
                </div>

                <label className="mmh-epd-message-field">
                  <span>Message</span>
                  <textarea
                    ref={messageFieldRef}
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    aria-label="Message to partner"
                  />
                </label>

                <div className="mmh-epd-message-actions">
                  <button
                    type="button"
                    className="mmh-epd-secondary-button"
                    onClick={() => setMessage("")}
                    disabled={!message}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="mmh-epd-primary-button"
                    onClick={handleSendMessage}
                    disabled={!message.trim()}
                  >
                    <Send size={16} />
                    Send Message
                  </button>
                </div>
              </section>
            )}
          </div>
        </section>

      </main>
    </div>
  );
};

export default HiringProfileDetailPage;
